import { useMemo } from "react";
import { Route, Routes } from "react-router-dom";
import { resolveShell } from "@/routes";
import { useAuthStore } from "@/stores/auth-store";
import { useAuthContext } from "@/hooks/useAuthContext";
import { STRINGS } from "@/constants/strings";
import { TenantShell } from "@/shells/TenantShell";
import { AdminShell } from "@/shells/AdminShell";
import { PatientShell } from "@/shells/PatientShell";
import LoginPage from "@/pages/auth/LoginPage";
import SetPasswordPage from "@/pages/auth/SetPasswordPage";
import ForgotPasswordPage from "@/pages/auth/ForgotPasswordPage";
import MfaChallengePage from "@/pages/auth/MfaChallengePage";
import MfaSetupPage from "@/pages/auth/MfaSetupPage";
import { LoaderCircleIcon } from "lucide-react";

function LoadingScreen() {
  return (
    <div className="flex min-h-svh items-center justify-center">
      <LoaderCircleIcon className="size-8 animate-spin text-muted-foreground" />
    </div>
  );
}

function TenantNotFound() {
  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-4 p-6 text-center">
      <h1 className="text-2xl font-bold">{STRINGS.shell.unknownTenant}</h1>
      <p className="text-muted-foreground">
        {STRINGS.shell.unknownTenantDescription}
      </p>
    </div>
  );
}

export function App() {
  const { shell } = useMemo(
    () => resolveShell(window.location.hostname),
    [],
  );

  const isInitializing = useAuthStore((s) => s.isInitializing);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const step = useAuthStore((s) => s.step);
  const { tenantError } = useAuthContext();
  const setStep = useAuthStore((s) => s.setStep);

  if (isInitializing) {
    return <LoadingScreen />;
  }

  if (tenantError) {
    if (tenantError.type === "not_found" || tenantError.type === "missing_slug") {
      return <TenantNotFound />;
    }
  }

  if (!isAuthenticated) {
    if (step === "force_change_password") {
      return <SetPasswordPage />;
    }

    if (step === "mfa_challenge") {
      return <MfaChallengePage />;
    }

    if (step === "mfa_setup") {
      return <MfaSetupPage />;
    }

    if (step === "forgot_password" || step === "forgot_password_confirm") {
      return (
        <ForgotPasswordPage onBackToLogin={() => setStep("login")} />
      );
    }

    return (
      <LoginPage onForgotPassword={() => setStep("forgot_password")} />
    );
  }

  return (
    <Routes>
      <Route
        path="/*"
        element={
          shell === "admin" ? (
            <AdminShell />
          ) : shell === "patient" ? (
            <PatientShell />
          ) : (
            <TenantShell />
          )
        }
      />
    </Routes>
  );
}
